'use client'

import React, {Children, type ReactElement, cloneElement, createContext, useContext, useState} from 'react'
import * as Portal from '@radix-ui/react-portal'

import {cn} from "@isudio/utils";

type DialogStackContextType = {
  totalDialogs: number
  setTotalDialogs: (n: number) => void
  isOpen: boolean
  setIsOpen: (v: boolean) => void
}

const DialogStackContext = createContext<DialogStackContextType | null>(null)

export function useDialogStack() {
  const ctx = useContext(DialogStackContext)
  if (!ctx) throw new Error('DialogStack not found')
  return ctx
}

type DialogStackProps = {
  open: boolean
  children: React.ReactNode
  dialogs: any[]
  closeDialog: () => void
}

export function DialogStack({
                              open,
                              children,
                              dialogs,
                              closeDialog,
                            }: DialogStackProps) {
  const [totalDialogs, setTotalDialogs] = useState(0)
  const [isOpen, setIsOpen] = useState(open)

  return (
    <DialogStackContext.Provider
      value={{ totalDialogs: dialogs.length, setTotalDialogs: () => {}, isOpen, setIsOpen }}
    >
      <Portal.Root>
        {/* Overlay only once, belongs to topmost dialog */}
        {dialogs.length > 0 && (
          <div
            className="fixed inset-0 z-40 bg-black/80"
            onWheel={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() => {
              const top = dialogs[dialogs.length - 1]
              if (!top?.props?.dialog?.modal) {
                closeDialog()
              }
            }}
          />
        )}

        <div className="pointer-events-none fixed inset-0 z-50 mx-auto flex w-full max-w-lg flex-col items-center justify-center">
          <div className="pointer-events-auto relative flex w-full flex-col items-center justify-center">
            {Children.map(children, (child, index) => {
              const childElement = child as ReactElement<{ index: number }>
              return cloneElement(childElement, {
                ...childElement.props,
                index,
              })
            })}
          </div>
        </div>
      </Portal.Root>
    </DialogStackContext.Provider>
  )
}

export function DialogStackBody({
                                  children,
                                  ...props
                                }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={cn(
        'pointer-events-none fixed inset-0 z-50 flex items-center justify-center',
        props.className
      )}
    >
      <div className="pointer-events-auto relative">{children}</div>
    </div>
  )
}

export function DialogStackContent({
                                     children,
                                     index = 0,
                                     ...props
                                   }: React.HTMLAttributes<HTMLDivElement> & { index?: number }) {
  return (
    <div
      {...props}
      style={{ zIndex: 50 + index * 10 }}
      className={cn(`relative`, props.className)}
    >
      {children}
    </div>
  )
}
